import React, { useEffect, useMemo, useState } from 'react';
import { Package, Smartphone, Trash2, RefreshCw, ArrowLeftRight, CircleAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { DistributionConfirmModal } from './DistributionConfirmModal';
import type { RemovalOperatorMismatch } from '@shared/task-distribution/removalOperatorMismatch';

export interface StoreReassignOperatorOption {
  id: number;
  name: string;
}

interface StoreAssignmentCardProps {
  storeId: number | string;
  storeName: string;
  operatorId: number | string;
  operatorName?: string;
  cycleId: number | string;
  assignmentCount: number;
  children: React.ReactNode;
  printMode?: boolean;
  onRefresh?: () => void;
  onReassigned: (result: { storeName: string; operatorName: string }) => void;
  /** Whether this store has been sent to mobile (has mobile_assignments) */
  isSent?: boolean;
  operatorMismatches?: RemovalOperatorMismatch[];
  reassignOperators?: StoreReassignOperatorOption[];
}

export function StoreAssignmentCard({
  storeId,
  storeName,
  operatorId,
  operatorName,
  cycleId,
  assignmentCount,
  children,
  printMode = false,
  onRefresh,
  onReassigned,
  isSent = false,
  operatorMismatches = [],
  reassignOperators = [],
}: StoreAssignmentCardProps) {
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReassignConfirm, setShowReassignConfirm] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [selectedReassignOperatorId, setSelectedReassignOperatorId] = useState<string>('');
  const [sent, setSent] = useState(isSent);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const availableReassignOperators = useMemo(
    () => reassignOperators.filter((operator) => String(operator.id) !== String(operatorId)),
    [operatorId, reassignOperators],
  );
  const reassignContextAvailable = !printMode && Boolean(cycleId && storeId && operatorId);
  const reassignSelectDisabled = !reassignContextAvailable || reassigning || availableReassignOperators.length === 0;

  const { data: reassignEligibility, isError: reassignEligibilityError, error: reassignError, isFetching: reassignFetching } = useQuery({
    queryKey: ['reassign-store-eligibility', cycleId, storeId, operatorId],
    // A full cycle can render hundreds of cards. Check only the store the
    // admin opens; selection still waits for the server's eligibility result.
    enabled: reassignOpen && !reassignSelectDisabled,
    staleTime: 0,
    retry: 1,
    queryFn: async () => {
      const params = new URLSearchParams({
        cycleId: String(cycleId),
        storeId: String(storeId),
        fromOperatorId: String(operatorId),
      });
      const response = await apiFetch(
        `/api/assignments/reassign-store/eligibility?${params.toString()}`,
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to load reassign eligibility');
      }
      return response.json() as Promise<{
        eligible: boolean;
        message: string | null;
        postingStarted: boolean;
      }>;
    },
  });

  const canReassign = reassignEligibility?.eligible === true && !reassignFetching && !reassignEligibilityError;
  const postingStarted = reassignEligibility?.postingStarted ?? false;
  const reassignDisabledReason = reassignEligibility?.message ?? null;

  useEffect(() => {
    setSent(isSent);
  }, [isSent]);

  const selectedReassignOperator = availableReassignOperators.find(
    (operator) => String(operator.id) === selectedReassignOperatorId,
  );

  const handleSend = async () => {
    setSending(true);
    try {
      const response = await apiFetch('/api/assignments/send-store', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cycleId: Number(cycleId),
          operatorId: Number(operatorId),
          storeId: Number(storeId),
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Send failed');
      }

      setSent(true);
      toast({
        title: '✅ Store Sent Successfully',
        description: `${result.snapshotCount} tasks sent to mobile for ${storeName}`,
        duration: 3000,
      });

      queryClient.invalidateQueries({ queryKey: ['mobile-assignments-summary', cycleId] });
      onRefresh?.();
    } catch (error: any) {
      console.error('Failed to send store:', error);
      toast({
        title: '❌ Send Failed',
        description: error?.message || 'Unknown error',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await apiFetch('/api/assignments/store', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cycleId: Number(cycleId),
          operatorId: Number(operatorId),
          storeId: Number(storeId),
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle blocked by signature
        if (result.signedStore) {
          toast({
            title: '❌ Cannot Delete',
            description: `${result.signedStore.storeName} has a manager signature (signed by ${result.signedStore.signedBy || 'manager'})`,
            variant: 'destructive',
            duration: 8000,
          });
          return;
        }
        throw new Error(result.error || 'Delete failed');
      }

      setSent(false);
      toast({
        title: '✅ Store Deleted Successfully',
        description: `Deleted ${result.deleted.mobileAssignments} assignments, ${result.deleted.postingEvents} submissions`,
        duration: 5000,
      });

      queryClient.invalidateQueries({ queryKey: ['mobile-assignments-summary', cycleId] });
      onRefresh?.();
    } catch (error: any) {
      console.error('Failed to delete store:', error);
      toast({
        title: '❌ Delete Failed',
        description: error?.message || 'Unknown error',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedReassignOperator || !canReassign) {
      return;
    }

    setReassigning(true);
    try {
      const response = await apiFetch('/api/assignments/reassign-store', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cycleId: Number(cycleId),
          storeId: Number(storeId),
          fromOperatorId: Number(operatorId),
          toOperatorId: selectedReassignOperator.id,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Reassign failed');
      }

      if (result.success !== true || result.sendToMobile?.sent !== true) {
        throw new Error('Reassignment was not fully confirmed. Refresh the store and check its operator before trying again.');
      }

      onReassigned({ storeName, operatorName: selectedReassignOperator.name });

      setSelectedReassignOperatorId('');
      queryClient.invalidateQueries({ queryKey: ['mobile-assignments-summary', cycleId] });
      queryClient.invalidateQueries({ queryKey: ['reassign-store-eligibility', cycleId] });
      onRefresh?.();
    } catch (error: any) {
      console.error('Failed to reassign store:', error);
      toast({
        title: '❌ Reassign Failed',
        description: error?.message || 'Unknown error',
        variant: 'destructive',
        duration: 8000,
      });
    } finally {
      setReassigning(false);
    }
  };

  const reassignDisabledLabel = !postingStarted
    ? (availableReassignOperators.length === 0
      ? 'No other operators available'
      : reassignDisabledReason
        ?? (reassignEligibilityError
          ? `Error: ${reassignError?.message || 'Eligibility check failed'}`
          : !reassignContextAvailable
            ? `Missing context (cycle: ${cycleId || 'none'})`
            : reassignOpen && reassignFetching
              ? 'Checking eligibility…'
              : null))
    : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {!printMode && <Package className="h-5 w-5" />}
              {storeName}
              {!printMode && (
                <span className="text-sm font-normal text-gray-500">
                  {assignmentCount} assignments
                </span>
              )}
            </CardTitle>
            {!printMode && (
              <CardDescription>
                Store ID: {storeId} | Operator ID: {operatorId}
              </CardDescription>
            )}
          </div>

          {/* Store-level Reassign / Send / Delete buttons */}
          {!printMode && (
            <div className="flex items-center gap-2">
              <Select
                open={reassignOpen}
                onOpenChange={setReassignOpen}
                value={selectedReassignOperatorId}
                onValueChange={(value) => {
                  // Radix also calls this for typeahead on a closed trigger.
                  // A cached result must not bypass opening a fresh check.
                  if (!reassignOpen || !canReassign) return;
                  setSelectedReassignOperatorId(value);
                  setShowReassignConfirm(true);
                }}
                disabled={reassignSelectDisabled}
              >
                <SelectTrigger
                  aria-label={`Reassign ${storeName}`}
                  className="w-[170px] h-8"
                  title={
                    reassignDisabledReason
                    ?? (availableReassignOperators.length === 0
                      ? 'No other operators available'
                      : 'Reassign this store slice to another operator')
                  }
                >
                  <SelectValue placeholder="Reassign" />
                </SelectTrigger>
                <SelectContent>
                  {reassignFetching ? (
                    <div role="status" className="px-3 py-2 text-sm text-muted-foreground">Checking eligibility…</div>
                  ) : reassignEligibilityError ? (
                    <div role="alert" className="px-3 py-2 text-sm text-destructive">
                      Could not check eligibility. Close and reopen Reassign to try again.
                    </div>
                  ) : !canReassign ? (
                    <div role="status" className="px-3 py-2 text-sm text-muted-foreground">
                      {reassignDisabledReason ?? 'Eligibility has not been confirmed.'}
                    </div>
                  ) : availableReassignOperators.map((operator) => (
                    <SelectItem key={operator.id} value={String(operator.id)}>
                      {operator.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {reassignDisabledLabel && (
                <span className="text-xs text-muted-foreground">{reassignDisabledLabel}</span>
              )}

              {/* Send button */}
              <Button
                onClick={() => setShowSendConfirm(true)}
                disabled={sending || sent}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400"
                title={sent ? 'Already sent' : `Send ${assignmentCount} tasks to mobile`}
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Smartphone className="w-3 h-3 mr-1" />
                    {sent ? 'Sent' : 'Send'}
                  </>
                )}
              </Button>

              {/* Delete button - only show if sent */}
              {sent && (
                <Button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  title="Delete all assignments and submissions for this store"
                >
                  {deleting ? (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </>
                  )}
                </Button>
              )}

              {sent && (
                <span className="text-xs text-green-700">✓ Sent</span>
              )}

              {postingStarted && (
                <Badge
                  variant="outline"
                  className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-50"
                  title={
                    reassignDisabledReason
                    ?? 'Posting has started at this store for this operator. Reassign is disabled.'
                  }
                >
                  <CircleAlert className="w-3 h-3 mr-1" />
                  Posting started
                </Badge>
              )}

              {canReassign && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <ArrowLeftRight className="w-3 h-3" />
                  Formats transfer
                </span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      {!printMode && postingStarted && (
        <div className="px-6 pb-2">
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Posting has started for this operator at this store. Reassign is disabled — check Proof of
            Posting for live progress.
          </p>
        </div>
      )}
      {!printMode && operatorMismatches.length > 0 && (
        <div className="px-6 pb-2 space-y-2">
          {operatorMismatches.map((m) => (
            <p
              key={`${m.mediaFormatId}-${m.postingOperatorId}-${m.removalOperatorId}`}
              className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
            >
              Format conflict: <strong>{m.mediaFormatName}</strong>
              {' — posting with '}
              <strong>{m.postingOperatorName}</strong>
              {' but removals with '}
              <strong>{m.removalOperatorName}</strong>.
            </p>
          ))}
        </div>
      )}
      <CardContent>{children}</CardContent>

      {/* Send Confirmation Modal */}
      <DistributionConfirmModal
        open={showSendConfirm}
        onOpenChange={setShowSendConfirm}
        method="mobile"
        operatorName={storeName}
        taskCount={assignmentCount}
        onConfirm={async () => {
          await handleSend();
          setShowSendConfirm(false);
        }}
      />

      {/* Delete Confirmation Modal */}
      <DistributionConfirmModal
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        method="delete"
        operatorName={storeName}
        taskCount={assignmentCount}
        onConfirm={async () => {
          await handleDelete();
          setShowDeleteConfirm(false);
        }}
      />

      {/* Reassign Confirmation Modal */}
      <DistributionConfirmModal
        open={showReassignConfirm}
        onOpenChange={(open) => {
          setShowReassignConfirm(open);
          if (!open && !reassigning) {
            setSelectedReassignOperatorId('');
          }
        }}
        method="reassign"
        operatorName={storeName}
        taskCount={assignmentCount}
        reassignFromOperatorName={operatorName ?? `Operator ${operatorId}`}
        reassignToOperatorName={selectedReassignOperator?.name}
        onConfirm={async () => {
          await handleReassign();
          setShowReassignConfirm(false);
        }}
      />
    </Card>
  );
}

export default StoreAssignmentCard;
